import { useEffect, useReducer, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import BadgeDTO from "../dto/BadgeDTO";
import ProjectPositionDTO from "../dto/ProjectPositionDTO";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import VacationReport from "../models/VacationReport";
import badgeService from "../services/BadgeService";
import OpsUserService from "../services/OpsUserService";
import TimeoffService from "../services/TimeoffService";
import UserService from "../services/UserService";
import { Routes } from "../types/Routes";
import RoleUtils from "../utils/RoleUtils";
import { useFeatureToggle } from "../external/ensolvers-core-frontend-mui/hooks/useFeatureToggle";
import { Features } from "../utils/Features";
import featureToggleList from "./../utils/toggleConfig.json";
import AsyncAuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AsyncAuthService";
import ApiError from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiError";

const SHOW_CARDS = true;

const usePersonalInfo = () => {
  const history = useHistory();
  const { paramExternalId } = useParams<{ paramExternalId?: string }>();

  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isProfileOwner, setIsProfileOwner] = useState<boolean>(false);
  const [isRequestPending, setIsRequestPending] = useState<boolean>(false);
  const [user, setUser] = useState<EnsolversOpsUser | undefined>(undefined);
  const [userUpdate, setUserUpdate] = useState<EnsolversOpsUser | undefined>(undefined);
  const [userAvatarUpdate, setUserAvatarUpdate] = useState<EnsolversOpsUser | undefined>(undefined);
  const [userAvatarRemove, setUserAvatarRemove] = useState<EnsolversOpsUser | undefined>(undefined);
  const [vacationReport, setVacationReport] = useState<VacationReport | undefined>(undefined);
  const [badges, setBadges] = useState<BadgeDTO[]>([]);
  const [projectPositions, setProjectPositions] = useState<ProjectPositionDTO[]>([]);
  const [isVacationReportEnabled] = useFeatureToggle(featureToggleList, Features.VACATION_REPORT);
  const [isCertificationListEnabled] = useFeatureToggle(featureToggleList, Features.CERTIFICATIONS);
  const [avatarUpdate, forceAvatarUpdate] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    setCanEdit(RoleUtils.isAdmin());
    loadUserData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user?.externalId && user?.externalId != paramExternalId && !isLoading) {

      Promise.all([UserService.getProjectPositions(user.externalId)]).then(([projectPositionData]) => {
        setProjectPositions(projectPositionData.getContentOrThrowError());
      });
      
      // If Certifications feature is enabled, it returns the list or the paged based on SHOW_CARDS value.
      isCertificationListEnabled && getUserBadges(user?.externalId);
      // If vacationReport feature is enabled fetch vacations information
      isVacationReportEnabled && getUserVacationReport(user);
      
      updateCurrentHistoryEntry(user.externalId);
    }
  }, [user, isLoading, paramExternalId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateCurrentHistoryEntry = (externalId: string) => {
      if (paramExternalId != user?.externalId) {
        history.replace(`${Routes.PROFILE}/${externalId}`);
      }
  };

  const getBadgesPagedForUser = async (page?: number, pageSize?: number): Promise<PagedResult<BadgeDTO>> => {
    let badges: PagedResult<BadgeDTO> = {
      content: [],
      pageNumber: 0,
      totalElements: 0,
    };
    if (user) {
      badges = (
        await badgeService.getBadgesPagedForOpsUser(page ? page : 0, pageSize ? pageSize : 100, user!.externalId!)
      ).getContentOrThrowError();
    }
    return badges;
  };

  const getUserDataSize = (user: EnsolversOpsUser) => {
    // We use this function to get the number of additional fields in personal data of the given user
    const additionalVacationsField = user.additionalVacationsAmount && user.additionalVacationsAmount > 0 ? 1 : 0;
    const initialTakenVacationsField = user.initialTakenVacations && user.initialTakenVacations > 0 ? 1 : 0;
    const vacationsComputedDateField = user.vacationsComputedSince ? 1 : 0;
    return additionalVacationsField + initialTakenVacationsField + vacationsComputedDateField;
  };

  const loadUserData = () => {
    setIsLoading(true);
    // fetch user based on param externalId
    if (paramExternalId) {
      UserService.getByExternalId(paramExternalId).then((response) => {
        setUser(response.getContentOrThrowError());
        // verify if fetch user data belongs to logged user
        AsyncAuthService.getUser().then(user => {
          if (user?.externalId === paramExternalId) {
            setIsProfileOwner(true)
          } else {
            setIsProfileOwner(false);
          }
        })
      });

      Promise.all([UserService.getProjectPositions(paramExternalId)]).then(([projectPositionData]) => {
        setProjectPositions(projectPositionData.getContentOrThrowError());
      });

      // If Certifications feature is enabled, returns the list or the paged based on SHOW_CARDS value.
      if (isCertificationListEnabled) {
        getUserBadges(paramExternalId);
      } else {
        setIsLoading(false);
      }
    } else {
      // if no param is present fetch the logged user data
      UserService.findByCoreUserId(AuthService.getUser()?.externalId!).then((response) => {
        setUser(response.getContentOrThrowError());
        setIsProfileOwner(true);
        setIsLoading(false);
      });
    }
  };
  // TODO return a promise to reduce repetition of setIsLoading(false)
  const getUserBadges = (userExternalId: string) => {
    SHOW_CARDS
      ? Promise.all([getBadgesPagedForUser()]).then(([badgesData]) => {
        setBadges(badgesData.content);
        setIsLoading(false);
      })
      : Promise.all([badgeService.getBadgesForOpsUserExternalId(userExternalId)]).then(([badgesData]) => {
        setBadges(badgesData.getContent());
        setIsLoading(false);
      });
  }

  const getUserVacationReport = (instance: EnsolversOpsUser) => {
    if (instance.externalId && instance.vacationsComputedSince) {
      TimeoffService.getVacationBalance(instance.externalId, new Date().getFullYear(), new Date().getMonth() + 1)
        .then((response) => {
          setVacationReport(response.getContentOrThrowError());
        })
        .catch((err) => NotificationService.error(err.message));
    }
  };

  const handleOnRemoveAvatar = async () => {
    if (!!userAvatarRemove) {
      if (userAvatarRemove.avatarUrl) {
        userAvatarRemove.avatarUrl = "";
        await OpsUserService.update(userAvatarRemove);
      }

      NotificationService.info("Avatar removed!");
      setUserAvatarRemove(undefined);
    }
  };

  const handleOnClickRemoveAvatarButton = () => {
    if (user?.avatarUrl) {
      setUserAvatarRemove(user);
    } else {
      NotificationService.error("No avatar to remove!");
    }
  };

  const validUser = (ensolversOpsUser: EnsolversOpsUser): string => {
    if (new Date(ensolversOpsUser.vacationsComputedSince!) < new Date(ensolversOpsUser.shortStart)) {
      return "Vacations Computed Since must be equal or greater than Start Date";
    }
    if (ensolversOpsUser.vacationAccumulation! < 0 || ensolversOpsUser.vacationAccumulation! > 30) {
      return "Vacations accumulation per year should be positive and less or equal than 30 days";
    }
    return "OK";
  };

  const onSave = async (ensolversOpsUser: EnsolversOpsUser) => {
    setIsRequestPending(true);
    const userValidationMessage = validUser(ensolversOpsUser);
    if (userValidationMessage != "OK") {
      setUserUpdate(undefined);
      NotificationService.error(userValidationMessage);
    } else {
      OpsUserService.update(ensolversOpsUser)
        .then(() => {
          NotificationService.info("User updated!");
          setUser(ensolversOpsUser); // reload state
        })
        .catch((error) => {
          if (error instanceof ApiError) {
            NotificationService.error(`Error: ${error.message}`)
          } else {
            NotificationService.error(`An error ocurred.`)
          }
        })
        .finally(() => {
          setUserUpdate(undefined);
          setIsRequestPending(false);
        });
    }
  };

  const clearUserUpdate = () => setUserUpdate(undefined);

  const showAdditionalVacationAmount = () => {
    return user?.additionalVacationsAmount! > 0 || user?.shortStart != user?.vacationsComputedSince;
  };

  return {
    history,
    user,
    setUser,
    isLoading,
    isProfileOwner,
    canEdit,
    handleOnClickRemoveAvatarButton,
    badges,
    projectPositions,
    userAvatarUpdate,
    setUserAvatarUpdate,
    userUpdate,
    setUserUpdate,
    setIsLoading,
    userAvatarRemove,
    setUserAvatarRemove,
    handleOnRemoveAvatar,
    onSave,
    clearUserUpdate,
    showAdditionalVacationAmount,
    vacationReport,
    getUserDataSize,
    isRequestPending,
    getBadgesPagedForUser,
    SHOW_CARDS,
    isCertificationListEnabled,
    updateCurrentHistoryEntry,
    paramExternalId
  };
};

export default usePersonalInfo;
